import {
  createTheme,
  PaletteColor,
  PaletteColorOptions,
  ThemeOptions,
  ThemeProvider,
  ComponentsOverrides,
  ComponentsVariants,
  Breakpoint,
} from '@mui/material/styles';
import React, {
  CSSProperties,
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { deepmerge } from '@mui/utils';
import {
  ContentPresets,
  CustomThemeSettings,
  GlobalComponentPresets,
  RecursivePartial,
  BaseButtonProps,
  THEME_PREFIX,
} from './types';
import { produce } from 'immer';
import { isWhite } from '../utils/stringUtils';
import brandclub_colors from '../colors/brandclubColors';
import { getDefaultThemePalette } from './defaultTheme';

export type ResponsiveCSSProperties = {
  [key in Breakpoint]?: CSSProperties;
};

export interface ComponentCSSOverrides {
  [componentName: string]: ResponsiveCSSProperties;
}

export interface CustomTheme {
  /**
   * Specifies the DTC theme mode.
   * @default 'inpage'
   * @description
   * - `'inpage'` is for components that are displayed inline with the host's page content.
   * - `'checkout-bar'` (legacy) is for components that are displayed in the checkout bar.
   * - `undefined` when not using a DTC theme.
   */
  dtcMode?: 'inpage' | 'checkout-bar';
  /**
   * global component presets applied to all components of the same type
   */
  globalComponentPresets?: GlobalComponentPresets;
  /**
   * Override the default css for specific components.
   */
  componentCSSOverrides?: ComponentCSSOverrides;
  /**
   * Settings that dictate what is displayed and how it is displayed
   */
  settings?: CustomThemeSettings;
  /**
   * content to be displayed
   */
  content?: ContentPresets;
  /**
   * custom css to be applied to the theme
   */
  customCss?: string;
}

export type CustomThemeOptions = RecursivePartial<CustomTheme>;

declare module '@mui/material/styles' {
  // The theme variables type is not enabled by default. You need to enable the typings: https://v6.mui.com/material-ui/customization/css-theme-variables/usage/#typescript
  interface CssThemeVariables {
    enabled: true;
  }
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface Theme extends CustomTheme {}
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface ThemeOptions extends CustomThemeOptions {}
  interface Palette {
    tertiary: PaletteColor;
    accent: PaletteColor;
    default: PaletteColor;
  }

  interface PaletteOptions {
    tertiary?: PaletteColorOptions;
    accent?: PaletteColorOptions;
    default?: PaletteColorOptions;
  }
  interface TypeBackground {
    /** the default background color. This is the color of the webpage background. */
    default: string;
    /** the background color of a card/dialog component. basically elevated components use this color. */
    paper: string;
    /** an alternate background color for smart banners, sections, etc. */
    alternate?: string;
  }

  interface ComponentNameToClassKey {
    BCSolidButton: 'root';
    BCOutlineButton: 'root';
    BCTextButton: 'root';
    BCIconButton: 'root';
  }

  interface ComponentsPropsList {
    BCSolidButton: Partial<BaseButtonProps>;
    BCOutlineButton: Partial<BaseButtonProps>;
    BCTextButton: Partial<BaseButtonProps>;
    BCIconButton: Partial<BaseButtonProps>;
  }

  interface Components {
    BCSolidButton?: {
      defaultProps?: ComponentsPropsList['BCSolidButton'];
      styleOverrides?: ComponentsOverrides<Theme>['BCSolidButton'];
      variants?: ComponentsVariants['BCSolidButton'];
    };
    BCOutlineButton?: {
      defaultProps?: ComponentsPropsList['BCOutlineButton'];
      styleOverrides?: ComponentsOverrides<Theme>['BCOutlineButton'];
      variants?: ComponentsVariants['BCOutlineButton'];
    };
    BCTextButton?: {
      defaultProps?: ComponentsPropsList['BCTextButton'];
      styleOverrides?: ComponentsOverrides<Theme>['BCTextButton'];
      variants?: ComponentsVariants['BCTextButton'];
    };
    BCIconButton?: {
      defaultProps?: ComponentsPropsList['BCIconButton'];
      styleOverrides?: ComponentsOverrides<Theme>['BCIconButton'];
      variants?: ComponentsVariants['BCIconButton'];
    };
  }

  interface TypographyVariants {
    xsButton: React.CSSProperties;
    smButton: React.CSSProperties;
    // default button is considered medium
    lgButton: React.CSSProperties;
  }

  // allow configuration using `createTheme()`
  interface TypographyVariantsOptions {
    xsButton?: React.CSSProperties;
    smButton?: React.CSSProperties;
    // default button is considered medium
    lgButton?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    xsButton: true;
    smButton: true;
    lgButton: true;
  }
}

/**
 * Palette colours MUI does not know about.
 *
 * `createTheme` fills in and augments only the keys IT defines — primary,
 * secondary, error, warning, info, success. `tertiary`, `accent` and `default`
 * are OURS, added through module augmentation, so a theme that simply omits one
 * produces a palette where that key is missing at runtime.
 *
 * The augmentation declares them REQUIRED on `Palette` while leaving them
 * optional on `PaletteOptions`, so every consumer reads `palette.tertiary.main`
 * unguarded and the compiler sees nothing wrong. That is not a hypothetical:
 * `QuantityControl` crashed on the Stackline Merch storefront with
 * "Cannot read properties of undefined (reading 'main')" because that brand's
 * theme never set a tertiary colour. PowerStep has the same gap and was one
 * click from the same error.
 *
 * Filling them here rather than at each call site keeps the type honest: after
 * this, `palette.tertiary` really is always present, which is what the 14 places
 * that dereference it already assume.
 */
const CUSTOM_PALETTE_KEYS = ['tertiary', 'accent', 'default'] as const;

const processTheme = (themeOptions: ThemeOptions | undefined) => {
  if (!themeOptions) return themeOptions;

  const sanitizedThemeOptions = produce(themeOptions, (draft) => {
    draft ??= {};

    // Remove customCss from the draft since it's not a valid Material-UI theme property
    delete (draft as any).customCss;

    /**
     * Recursively updates keys whose value corresponds to any URL.
     * a plain URL string causes the theme cssvars to fail to load which breaks the theme.
     * we wrap the URL in a css url() function to prevent the theme cssvars from failing to load.
     */
    const processThemeUrls = (obj: Record<string, unknown>) => {
      Object.keys(obj).forEach((key) => {
        const value = obj[key];
        if (typeof value === 'string' && value.match(/https?:\/\//)) {
          obj[key] = `url(${value})`;
          return;
        }
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          processThemeUrls((value ?? {}) as Record<string, unknown>);
        }
      });
    };

    processThemeUrls(draft);

    // Fix typography lineHeight to prevent malformed font shorthand CSS variables
    if (draft.typography && typeof draft.typography === 'object') {
      const typography = draft.typography as any;
      Object.keys(typography).forEach((key) => {
        const variant = typography[key];
        if (variant && typeof variant === 'object' && variant.lineHeight === 'inherit') {
          // Change 'inherit' to 'normal' to prevent malformed font shorthand
          variant.lineHeight = 'normal';
        }
      });
    }
    // A brand theme carries only what the brand set. Anything it left out falls
    // back to the Brandclub default, so the key exists by the time any
    // component reads it.
    const defaultPalette = getDefaultThemePalette();
    draft.palette ??= {};
    for (const key of CUSTOM_PALETTE_KEYS) {
      const current = draft.palette[key] as { main?: string } | undefined;
      if (!current || !current.main) {
        draft.palette[key] = defaultPalette[key];
      }
    }

    let backdropColor: string = brandclub_colors.black;
    if (themeOptions.palette?.text?.primary && !isWhite(themeOptions.palette?.text?.primary)) {
      backdropColor = themeOptions.palette.text.primary;
    }
    draft.components ??= {};
    draft.components.MuiBackdrop ??= {};
    draft.components.MuiBackdrop.styleOverrides ??= {};
    draft.components.MuiBackdrop.styleOverrides.root = {
      backgroundColor: `rgba(${backdropColor
        .replace('#', '')
        .match(/.{1,2}/g)
        ?.map((hex) => parseInt(hex, 16))
        .join(', ')}, 0.21)`,
      transition: 'opacity 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
      zIndex: 900,
    };
  });

  return sanitizedThemeOptions;
};

const UpdateCustomThemeContext = createContext<Dispatch<SetStateAction<ThemeOptions | undefined>> | null>(null);

const CustomThemeOptionsContext = createContext<ThemeOptions | undefined>(undefined);

export const useUpdateCustomTheme = () => {
  const _updateCustomTheme = useContext(UpdateCustomThemeContext);
  const customThemeOptions = useContext(CustomThemeOptionsContext);
  if (!_updateCustomTheme || !customThemeOptions) {
    throw new Error('useUpdateCustomTheme must be used within a CustomThemeProvider');
  }
  const updateCustomTheme = useCallback(
    (update: React.SetStateAction<ThemeOptions | undefined>, forceUpdate = false) => {
      if (forceUpdate) {
        return _updateCustomTheme(update);
      }
      return _updateCustomTheme((prev) =>
        deepmerge(prev, typeof update === 'function' ? update(prev) : update, { clone: true }),
      );
    },
    [_updateCustomTheme, customThemeOptions],
  );

  return [customThemeOptions, updateCustomTheme] as const;
};

type CustomThemeProviderProps = PropsWithChildren<{
  themeOptions: ThemeOptions | undefined;
}>;

const WithUpdateCustomThemeOptions = (props: CustomThemeProviderProps) => {
  const [themeOptions, setThemeOptions] = useState(props.themeOptions);
  useEffect(() => {
    setThemeOptions(props.themeOptions);
  }, [props.themeOptions]);
  return (
    <UpdateCustomThemeContext.Provider value={setThemeOptions}>
      <CustomThemeOptionsContext.Provider value={themeOptions}>{props.children}</CustomThemeOptionsContext.Provider>
    </UpdateCustomThemeContext.Provider>
  );
};

const CustomThemeProviderConsumer: FC<PropsWithChildren> = (props) => {
  const [themeOptions] = useUpdateCustomTheme();

  const theme = createTheme({
    ...processTheme(themeOptions),
    cssVariables: {
      cssVarPrefix: THEME_PREFIX,
    },
  });

  return <ThemeProvider theme={theme}>{props.children}</ThemeProvider>;
};

export const CustomThemeProvider = (props: CustomThemeProviderProps) => {
  return (
    <WithUpdateCustomThemeOptions themeOptions={props.themeOptions}>
      <CustomThemeProviderConsumer>{props.children}</CustomThemeProviderConsumer>
    </WithUpdateCustomThemeOptions>
  );
};
